import type { EditorRef } from 'react-email-editor';
import { PersonalFieldsByCategory } from '../../../../../../hooks/Marketing';
import { offerToken } from '../shared/offerTokens';

export type UnlayerEditor = NonNullable<EditorRef['editor']>;
export type UnlayerDesign = Parameters<UnlayerEditor['loadDesign']>[0];
export type UnlayerMergeTags = Parameters<UnlayerEditor['setMergeTags']>[0];
type UnlayerRow = UnlayerDesign['body']['rows'][number];

export const EDITOR_HEIGHT = 'clamp(400px, calc(100vh - 420px), 900px)';

export type InsertedBlock =
  | { kind: 'text'; text: string }
  | { kind: 'image'; alt: string; url: string; width: number; height: number };

const EDITABLE_FLAGS = { selectable: true, draggable: true, duplicatable: true, deletable: true, hideable: true };

const buildContent = (id: number, block: InsertedBlock) =>
  block.kind === 'image'
    ? {
        id: `cnt_${id}`,
        type: 'image',
        values: {
          src: { url: block.url, width: block.width, height: block.height, autoWidth: false, maxWidth: '100%' },
          altText: block.alt,
          action: { name: 'web', values: { href: '', target: '_blank' } },
          containerPadding: '10px',
          ...EDITABLE_FLAGS,
        },
      }
    : {
        id: `cnt_${id}`,
        type: 'text',
        values: {
          text: `<p style="font-size:14px;line-height:140%;">${block.text}</p>`,
          containerPadding: '10px',
          ...EDITABLE_FLAGS,
        },
      };

export const appendBlock = (design: UnlayerDesign, block: InsertedBlock): UnlayerDesign => {
  const id = Date.now();
  const row = {
    id: `row_${id}`,
    cells: [1],
    columns: [{ id: `col_${id}`, contents: [buildContent(id, block)], values: {} }],
    values: {
      displayCondition: null,
      columns: false,
      backgroundColor: '',
      columnsBackgroundColor: '',
      padding: '0px',
      ...EDITABLE_FLAGS,
    },
  } as unknown as UnlayerRow;
  return { ...design, body: { ...design.body, rows: [...(design.body.rows ?? []), row] } };
};

const toKey = (name: string) => name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

export const buildMergeTags = (offerCount: number, personalFields: PersonalFieldsByCategory): UnlayerMergeTags => {
  const offerTags: UnlayerMergeTags = {};
  for (let index = 0; index < Math.max(1, offerCount); index++) {
    offerTags[`offre_${index + 1}`] = { name: `Offre ${index + 1}`, value: offerToken(index) };
  }
  offerTags.num_carte = { name: 'Numéro de carte', value: '#NUM_CARTE#' };

  const tags: UnlayerMergeTags = { codes_operation: { name: 'Codes opération', mergeTags: offerTags } };
  Object.entries(personalFields).forEach(([category, fields]) => {
    tags[toKey(category)] = {
      name: category,
      mergeTags: Object.fromEntries(
        fields.map(field => [field.ANCHOR.replace(/[^a-zA-Z0-9]/g, '_'), { name: field.NAME, value: field.ANCHOR }]),
      ),
    };
  });
  return tags;
};
